import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { Library, Pencil, Upload, Download, FileDown, Palette, Code2, X, MessageSquarePlus, Globe, Frame as FrameIcon, Clapperboard } from 'lucide-react';
import { useCanvasStore, dispatch } from '../../store/canvas-store';
import { usePanelStore, PANEL_INSET } from './panel-store';
import { usePanelFrames, useGlobalChat } from './panelFrames';
import { panelChatScope } from '../chatTarget';
import { CodeFrameView } from '../code/CodeFrameView';
import { ChatFrameView } from '../chat/ChatFrameView';
import { ScriptEditor } from '../code/ScriptEditor';
import { TsxEditor } from '../code/TsxEditor';
import { TsxPropsForm } from './TsxPropsForm';
import { ThemeLibraryModal } from './ThemeLibraryModal';
import { ThemeEditorModal } from './ThemeEditorModal';
import { FrameHistorySection } from './FrameHistorySection';
import { LottieControls } from '../../lottie/LottieControls';
import { lottieElementsOf } from '../../lottie/scan';
import { getRenderHtml, framesById } from '../../store/commands';
import { newConversation } from '../../agent/chatTurn';
import { exportTheme, parseThemeJson } from '../../io/theme-io';
import { pickFile } from '../../io/download';
import type { TsxFreeFrame, TsxMainFrame, TsxInstanceFrame } from '../../store/types';
import type { EditorView } from '@codemirror/view';

/**
 * Docked right-rail editing surface. Overlays the canvas (no reflow). Edits the
 * single selected design frame's HTML on top (CodeFrameView over a panel-owned
 * code frame) and offers a frame-scoped agent chat on the bottom (ChatFrameView
 * over a per-frame panel-owned chat frame). Toggled via ⌘E / ActionChip.
 */
export function EditorPanel() {
  const open = usePanelStore((s) => s.open);
  const width = usePanelStore((s) => s.width);
  const chatRatio = usePanelStore((s) => s.chatRatio);
  const setOpen = usePanelStore((s) => s.setOpen);
  const setWidth = usePanelStore((s) => s.setWidth);
  const setChatRatio = usePanelStore((s) => s.setChatRatio);
  const chatGlobalOverride = usePanelStore((s) => s.chatGlobalOverride);
  const setChatGlobalOverride = usePanelStore((s) => s.setChatGlobalOverride);
  const editorOpen = usePanelStore((s) => s.editorOpen);
  const setEditorOpen = usePanelStore((s) => s.setEditorOpen);

  const selection = useCanvasStore((s) => s.selection);
  const layerSelection = useCanvasStore((s) => s.layerSelection);
  const frames = useCanvasStore((s) => s.doc.frames);
  const themes = useCanvasStore((s) => s.doc.themes) ?? [];
  const [tab, setTab] = useState<'html' | 'script'>('html');
  const [libraryOpen, setLibraryOpen] = useState(false);
  const [themeEditorId, setThemeEditorId] = useState<string | null>(null);
  const [themeMenuOpen, setThemeMenuOpen] = useState(false);

  const target =
    selection.length === 1 ? frames.find((f) => f.id === selection[0]) : undefined;
  const editable =
    target &&
    (target.kind === 'free' || target.kind === 'main' || target.kind === 'instance')
      ? target
      : undefined;
  // tsx* frames also use the panel for editing/chat.
  const tsxTarget =
    target &&
    (target.kind === 'tsxFree' || target.kind === 'tsxMain' || target.kind === 'tsxInstance')
      ? (target as TsxFreeFrame | TsxMainFrame | TsxInstanceFrame)
      : undefined;
  const panelTarget = editable ?? tsxTarget;

  // Lottie slot target: the selected layer if it's a data-lottie element on the
  // panel frame, else the panel frame's first data-lottie element. Drives the
  // Animation section. Gated to FREE and MAIN only: instances resolve to their
  // Main's html (so a lottie would appear) but LottieControls reads `'html' in
  // frame` (false for instances) and lottie.setSlots can't store slots on an
  // instance — the section would be dead/empty. Edit lottie slots on the Main.
  const lottieFrame =
    editable && (editable.kind === 'free' || editable.kind === 'main') ? editable : undefined;
  const lottieCfId = (() => {
    const f = lottieFrame;
    if (!f) return undefined;
    const specs = lottieElementsOf(getRenderHtml(f, framesById(frames)));
    if (specs.length === 0) return undefined;
    if (layerSelection?.frameId === f.id) {
      const hit = specs.find((s) => s.cfNodeId === layerSelection.cfNodeId);
      if (hit) return hit.cfNodeId;
    }
    return specs[0].cfNodeId;
  })();

  // For tsxInstance, look up the Main frame so we can show its source read-only.
  const tsxMain =
    tsxTarget?.kind === 'tsxInstance'
      ? (frames.find((f) => f.id === (tsxTarget as TsxInstanceFrame).mainId) as TsxMainFrame | undefined)
      : tsxTarget?.kind === 'tsxMain'
        ? (tsxTarget as TsxMainFrame)
        : undefined;

  // CodeMirror view handle for the TSX editor (cursor-aware operations).
  const tsxEditorViewRef = useRef<EditorView | null>(null);

  const { codeFrame, chatFrame } = usePanelFrames(panelTarget?.id);
  const globalChat = useGlobalChat();
  const canScopeToFrame = !!panelTarget && !!chatFrame;
  const scope = panelChatScope(chatGlobalOverride, canScopeToFrame);
  const activeChat = scope === 'frame' ? chatFrame : globalChat;

  // Scope follows the selection: selecting a different frame (or deselecting)
  // clears the ephemeral whole-canvas override so the chip switches threads.
  const targetIdForScope = panelTarget?.id;
  useEffect(() => {
    setChatGlobalOverride(false);
  }, [targetIdForScope, setChatGlobalOverride]);
  // The panel is chat-first: the editor region exists only when an editable
  // frame is selected AND the user has intentionally opened it (editorOpen).
  const canEdit = !!panelTarget && (!!tsxTarget || (!!editable && !!codeFrame));
  const hasEditor = canEdit && editorOpen;

  // Drag the left edge to resize. Width = distance from the right viewport edge.
  const dragging = useRef(false);
  const onResizeDown = useCallback(
    (e: React.PointerEvent) => {
      e.preventDefault();
      dragging.current = true;
      (e.target as Element).setPointerCapture(e.pointerId);
    },
    [],
  );
  const onResizeMove = useCallback(
    (e: React.PointerEvent) => {
      if (!dragging.current) return;
      setWidth(window.innerWidth - e.clientX - PANEL_INSET);
    },
    [setWidth],
  );
  const onResizeUp = useCallback((e: React.PointerEvent) => {
    dragging.current = false;
    try {
      (e.target as Element).releasePointerCapture(e.pointerId);
    } catch {
      /* not captured */
    }
  }, []);

  // Drag the horizontal divider to resize the editor/chat split. chatRatio =
  // chat-region height fraction, measured from the bottom of the content area.
  const contentRef = useRef<HTMLDivElement>(null);
  const splitting = useRef(false);
  const onSplitDown = useCallback((e: React.PointerEvent) => {
    e.preventDefault();
    splitting.current = true;
    (e.target as Element).setPointerCapture(e.pointerId);
  }, []);
  const onSplitMove = useCallback(
    (e: React.PointerEvent) => {
      if (!splitting.current || !contentRef.current) return;
      const r = contentRef.current.getBoundingClientRect();
      if (r.height <= 0) return;
      setChatRatio((r.bottom - e.clientY) / r.height);
    },
    [setChatRatio],
  );
  const onSplitUp = useCallback((e: React.PointerEvent) => {
    splitting.current = false;
    try {
      (e.target as Element).releasePointerCapture(e.pointerId);
    } catch {
      /* not captured */
    }
  }, []);

  // Import a design.md file as a theme.
  const fileRef = useRef<HTMLInputElement>(null);
  const onImportFile = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    file.text().then((source) => dispatch({ t: 'theme.importDesignMd', source }));
  }, []);

  const onImportThemeJson = useCallback(async () => {
    const file = await pickFile('.json');
    if (!file) return;
    try {
      const theme = parseThemeJson(await file.text());
      dispatch({ t: 'theme.create', theme });
      if (panelTarget) dispatch({ t: 'frame.setTheme', id: panelTarget.id, themeId: theme.id });
    } catch (err) {
      window.alert(`Import failed: ${err instanceof Error ? err.message : String(err)}`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [panelTarget]);

  const onExportTheme = useCallback(() => {
    const th = themes.find((t) => t.id === panelTarget?.themeId);
    if (th) exportTheme(th);
  }, [themes, panelTarget]);

  if (!open) return null;

  return (
    <aside
      className="fixed z-40 flex flex-col rounded-xl overflow-hidden bg-[var(--color-surface)] border border-[var(--color-border)] shadow-[var(--shadow-2)]"
      style={{ top: PANEL_INSET, right: PANEL_INSET, bottom: PANEL_INSET, width }}
      onMouseDown={(e) => e.stopPropagation()}
    >
      {/* Left-edge resize handle */}
      <div
        className="absolute top-0 left-0 bottom-0 cursor-col-resize"
        style={{ width: 6, marginLeft: -3 }}
        onPointerDown={onResizeDown}
        onPointerMove={onResizeMove}
        onPointerUp={onResizeUp}
      />

      {/* Header */}
      <div className="flex items-center justify-between px-3.5 py-3 text-[13px] border-b border-[var(--color-border)] text-[var(--color-text)]">
        <span className="truncate">
          {panelTarget ? (
            <>
              <span className="text-[var(--color-text-muted)]">Editing</span>{' '}
              <span className="font-mono font-medium">{panelTarget.title || panelTarget.id}</span>
            </>
          ) : (
            <span className="font-medium">Editor</span>
          )}
        </span>
        <div className="flex items-center gap-1.5">
          {canEdit && (
            <button
              type="button"
              onClick={() => setEditorOpen(!editorOpen)}
              title={editorOpen ? 'Hide code editor' : 'Edit code'}
              aria-label="Toggle code editor"
              aria-pressed={editorOpen}
              className={
                'p-1 rounded hover:bg-[var(--color-menu-hover)] flex items-center ' +
                (editorOpen ? 'text-[var(--color-tool-code)]' : '')
              }
              style={editorOpen ? undefined : { color: 'var(--color-text-muted)' }}
            >
              <Code2 size={14} />
            </button>
          )}
          {panelTarget && (
            <select
              value={panelTarget.themeId ?? ''}
              onChange={(e) =>
                dispatch({ t: 'frame.setTheme', id: panelTarget.id, themeId: e.target.value || undefined })
              }
              title="Frame theme (children inherit)"
              className="cf-select rounded px-1 py-0.5 text-xs bg-[var(--color-input-bg)] border border-[var(--color-input-border)] text-[var(--color-text)]"
            >
              <option value="">— theme —</option>
              {themes.map((t) => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </select>
          )}
          {/* All theme import/export/edit actions collapse into one menu so the
              header stays: title · frame-theme picker · theme menu · close. */}
          <div className="relative">
            <button
              type="button"
              onClick={() => setThemeMenuOpen((v) => !v)}
              title="Theme actions"
              aria-label="Theme actions"
              className="p-1 rounded hover:bg-[var(--color-menu-hover)] flex items-center"
              style={{ color: 'var(--color-text-muted)' }}
            >
              <Palette size={14} />
            </button>
            {themeMenuOpen && (
              <ThemeActionsMenu
                hasTheme={!!panelTarget?.themeId}
                onClose={() => setThemeMenuOpen(false)}
                onLibrary={() => setLibraryOpen(true)}
                onEditTokens={() => panelTarget?.themeId && setThemeEditorId(panelTarget.themeId)}
                onImportMd={() => fileRef.current?.click()}
                onImportJson={onImportThemeJson}
                onExportJson={onExportTheme}
              />
            )}
          </div>
          <input
            ref={fileRef}
            type="file"
            accept=".md,text/markdown"
            onChange={onImportFile}
            style={{ display: 'none' }}
          />
          <button
            type="button"
            onClick={() => setOpen(false)}
            title="Close panel (⌘E)"
            aria-label="Close panel"
            className="p-1 rounded hover:bg-[var(--color-menu-hover)] flex items-center"
            style={{ color: 'var(--color-text-muted)' }}
          >
            <X size={14} />
          </button>
        </div>
      </div>

      <div ref={contentRef} className="flex-1 min-h-0 flex flex-col">
        {/* Editor region — only when a single editable/tsx frame is selected */}
        {hasEditor && (tsxTarget ? (
          <div className="min-h-0 overflow-hidden flex flex-col" style={{ flex: `${1 - chatRatio} 1 0` }}>
            {(tsxTarget.kind === 'tsxFree' || tsxTarget.kind === 'tsxMain') ? (
              <div className="flex-1 min-h-0">
                <TsxEditor
                  value={tsxTarget.tsx}
                  viewRef={tsxEditorViewRef}
                  onChange={(tsx) => dispatch({ t: 'tsx.setSource', id: tsxTarget.id, tsx })}
                />
              </div>
            ) : (
              /* tsxInstance — read-only main source + props form */
              <div className="flex flex-col h-full min-h-0 overflow-y-auto">
                <div className="shrink-0" style={{ maxHeight: '50%', overflow: 'hidden' }}>
                  <TsxEditor value={tsxMain?.tsx ?? ''} readOnly />
                </div>
                {tsxTarget.kind === 'tsxInstance' && tsxMain && (
                  <TsxPropsForm
                    instance={tsxTarget as TsxInstanceFrame}
                    main={tsxMain as TsxMainFrame}
                  />
                )}
              </div>
            )}
          </div>
        ) : editable && codeFrame ? (
          <>
            {/* HTML / Script tabs */}
            <div className="flex shrink-0 text-[13px] border-b border-[var(--color-border)]">
              {(['html', 'script'] as const).map((t) => (
                <button
                  key={t}
                  type="button"
                  onClick={() => setTab(t)}
                  className={
                    'px-4 py-2 border-b-2 ' +
                    (tab === t
                      ? 'text-[var(--color-text)] border-[var(--color-accent)] font-semibold'
                      : 'text-[var(--color-text-muted)] border-transparent')
                  }
                >
                  {t === 'html' ? 'HTML' : 'Script'}
                </button>
              ))}
            </div>
            {/* Editor region */}
            <div className="min-h-0 overflow-hidden" style={{ flex: `${1 - chatRatio} 1 0` }}>
              {tab === 'html' ? (
                <CodeFrameView frame={codeFrame} />
              ) : editable.kind === 'instance' ? (
                <div
                  className="h-full flex items-center justify-center p-8 text-center text-sm leading-relaxed"
                  style={{ color: 'var(--color-text-muted)' }}
                >
                  Script lives on the Main. Edit the Main component to change behavior.
                </div>
              ) : (
                <ScriptEditor
                  value={editable.script ?? ''}
                  onChange={(script) => dispatch({ t: 'frame.setScript', id: editable.id, script })}
                />
              )}
            </div>
          </>
        ) : null)}

        {/* Drag divider — only when the editor region is present */}
        {hasEditor && (
          <div
            className="cursor-row-resize shrink-0"
            style={{ height: 6, background: 'var(--color-border)' }}
            onPointerDown={onSplitDown}
            onPointerMove={onSplitMove}
            onPointerUp={onSplitUp}
            title="Drag to resize editor / chat"
          />
        )}

        {/* Animation — Lottie slot controls for the selected lottie element.
            Free/Main only (see lottieFrame): instances edit slots on the Main. */}
        {lottieFrame && lottieCfId && (
          <div className="shrink-0 border-b border-[var(--color-border)]">
            <div className="px-3 py-2 text-[13px] font-medium text-[var(--color-text)] flex items-center gap-1.5">
              <Clapperboard size={14} className="opacity-75" />
              Animation
            </div>
            <LottieControls frameId={lottieFrame.id} cfNodeId={lottieCfId} />
          </div>
        )}

        {/* Per-frame version history — shown when a design frame is selected */}
        {panelTarget && <FrameHistorySection frameId={panelTarget.id} />}

        {/* Chat region — scope toggle + active conversation */}
        <div
          className="flex flex-col min-h-0"
          style={{ flex: hasEditor ? `${chatRatio} 1 0` : '1 1 0' }}
        >
          <div className="flex items-center">
            <div className="flex-1">
              <ChatScopeChip
                scope={scope}
                canScopeToFrame={canScopeToFrame}
                frameLabel={panelTarget ? (panelTarget.title || panelTarget.id) : ''}
                onOverride={setChatGlobalOverride}
              />
            </div>
            <button
              type="button"
              title="New conversation (clears the transcript + resets agent memory)"
              disabled={!activeChat}
              onClick={() => { if (activeChat) newConversation(activeChat.id); }}
              className="mr-2 flex items-center justify-center w-7 h-7 rounded-[6px] text-[var(--color-text-muted)] hover:bg-[var(--color-menu-hover)] hover:text-[var(--color-text)] disabled:opacity-40"
            >
              <MessageSquarePlus size={14} />
            </button>
          </div>
          {activeChat ? (
            <div className="flex-1 min-h-0">
              <ChatFrameView frame={activeChat} />
            </div>
          ) : (
            <div
              className="flex-1 flex items-center justify-center text-sm"
              style={{ color: 'var(--color-text-muted)' }}
            >
              Loading…
            </div>
          )}
        </div>
      </div>

      {libraryOpen && (
        <ThemeLibraryModal targetFrameId={panelTarget?.id} onClose={() => setLibraryOpen(false)} />
      )}
      {themeEditorId && <ThemeEditorModal themeId={themeEditorId} onClose={() => setThemeEditorId(null)} />}
    </aside>
  );
}

/** Scope chip above the chat region. One rule: chat scope follows the current
 *  selection (frame thread when a single design frame is selected, whole-canvas
 *  thread otherwise). The trailing button is the ephemeral un-scope / re-scope
 *  affordance; it never persists and resets when the selection changes. */
function ChatScopeChip({
  scope, canScopeToFrame, frameLabel, onOverride,
}: {
  scope: 'frame' | 'global';
  canScopeToFrame: boolean;
  frameLabel: string;
  onOverride: (on: boolean) => void;
}) {
  return (
    <div className="flex items-center gap-1 m-2 min-w-0">
      <span
        className="flex items-center gap-1.5 min-w-0 px-2 py-1 rounded-[6px] text-xs bg-[var(--color-surface-2)] border border-[var(--color-border)] text-[var(--color-text)]"
        title={scope === 'frame' ? `Chatting about ${frameLabel}` : 'Chatting about the whole canvas'}
      >
        {scope === 'frame' ? <FrameIcon size={12} /> : <Globe size={12} />}
        <span className="truncate">{scope === 'frame' ? frameLabel : 'Whole canvas'}</span>
      </span>
      {canScopeToFrame && (
        <button
          type="button"
          title={scope === 'frame' ? 'Chat about the whole canvas instead' : `Chat about ${frameLabel} instead`}
          onClick={() => onOverride(scope === 'frame')}
          className="flex items-center justify-center w-6 h-6 rounded-[6px] text-[var(--color-text-muted)] hover:bg-[var(--color-menu-hover)] hover:text-[var(--color-text)]"
        >
          {scope === 'frame' ? <Globe size={12} /> : <FrameIcon size={12} />}
        </button>
      )}
    </div>
  );
}

/** Popover holding the less-frequent theme actions (library, import/export,
 *  edit tokens), so the panel header stays quiet. Themed like the context menu. */
function ThemeActionsMenu({
  hasTheme,
  onClose,
  onLibrary,
  onEditTokens,
  onImportMd,
  onImportJson,
  onExportJson,
}: {
  hasTheme: boolean;
  onClose: () => void;
  onLibrary: () => void;
  onEditTokens: () => void;
  onImportMd: () => void;
  onImportJson: () => void;
  onExportJson: () => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) onClose();
    };
    window.addEventListener('mousedown', onDown);
    return () => window.removeEventListener('mousedown', onDown);
  }, [onClose]);

  const rows: { label: string; icon: ReactNode; onClick: () => void; show: boolean }[] = [
    { label: 'Browse library', icon: <Library size={14} />, onClick: onLibrary, show: true },
    { label: 'Edit theme tokens', icon: <Pencil size={14} />, onClick: onEditTokens, show: hasTheme },
    { label: 'Import design.md', icon: <FileDown size={14} />, onClick: onImportMd, show: true },
    { label: 'Import theme JSON', icon: <Upload size={14} />, onClick: onImportJson, show: true },
    { label: 'Export theme JSON', icon: <Download size={14} />, onClick: onExportJson, show: hasTheme },
  ];

  return (
    <div
      ref={ref}
      className="absolute top-[calc(100%+4px)] right-0 z-50 min-w-[184px] p-1 rounded-[6px] font-sans text-[13px] bg-[var(--color-menu-bg)] border border-[var(--color-menu-border)] shadow-[var(--shadow-2)]"
    >
      {rows.filter((r) => r.show).map((r) => (
        <button
          key={r.label}
          type="button"
          onClick={() => {
            r.onClick();
            onClose();
          }}
          className="flex items-center gap-2 w-full text-left px-2 py-1.5 rounded-[4px] border-0 bg-transparent cursor-pointer text-[var(--color-text)] hover:bg-[var(--color-menu-hover)]"
        >
          <span className="flex opacity-75">{r.icon}</span>
          <span className="flex-1">{r.label}</span>
        </button>
      ))}
    </div>
  );
}
